import {
	type CurrencyEnum,
	type GetProductSamplePricesQuery,
	useGetProductSamplePricesQuery,
} from '@codezo/graphql';
import { cn } from '../../lib/utils';
import { CurrencyText } from '../CurrencyText';
import { Radio } from '../ui/radio';

export type CatalogSampleRow = GetProductSamplePricesQuery['getProductSamplePrices'][number];

/** What a catalogue sample price puts in the sample form, in the form's currency. */
export type SampleFill = {
	baseSamplePrice: number;
	/** Null when the catalogue knows no shipping: the field is left alone, not zeroed. */
	baseShippingPrice: number | null;
	minProductionDays: number | null;
	maxProductionDays: number | null;
	deliveryDaysMin: number | null;
	deliveryDaysMax: number | null;
};

/**
 * A catalogue row in the form's currency, or null when there is no figure in it
 * (no rate for the pair). The rows are fetched converted into the form's
 * currency, so this reads `converted` and never relabels a stored amount.
 */
export function sampleRowFill(row: CatalogSampleRow, formCurrency: string): SampleFill | null {
	const figures =
		row.converted?.currency === formCurrency
			? row.converted
			: row.currency === formCurrency
				? row
				: null;
	if (!figures) return null;
	return {
		baseSamplePrice: figures.samplePrice,
		baseShippingPrice: figures.shippingPrice ?? null,
		minProductionDays: row.minProductionDays ?? null,
		maxProductionDays: row.maxProductionDays ?? null,
		deliveryDaysMin: row.deliveryDaysMin ?? null,
		deliveryDaysMax: row.deliveryDaysMax ?? null,
	};
}

/** Whether a row may price this offer: the default price, or the assigned supplier's own. */
const isEligible = (row: CatalogSampleRow, assignedVetterId?: string | null) =>
	!row.vetterEntityId || row.vetterEntityId === assignedVetterId;

/**
 * The price the catalogue would use for this offer: the assigned supplier's own
 * sample price, else the default one. The same rule the backend applies, so the
 * row marked here is the one drift compares against.
 */
export function bestSampleRow(
	rows: CatalogSampleRow[],
	assignedVetterId?: string | null
): CatalogSampleRow | null {
	return (
		(assignedVetterId && rows.find((r) => r.vetterEntityId === assignedVetterId)) ||
		rows.find((r) => !r.vetterEntityId) ||
		null
	);
}

/** "8–10", or "8" for a single day count. Null when unknown. Shared by every sample price view. */
export const formatDayRange = (min?: number | null, max?: number | null) =>
	min == null ? null : max == null || max === min ? `${min}` : `${min}–${max}`;

/** "8–10 + 2–3 days": production, then delivery. Null when neither is known. */
export function formatLeadTime(row: {
	minProductionDays?: number | null;
	maxProductionDays?: number | null;
	deliveryDaysMin?: number | null;
	deliveryDaysMax?: number | null;
}): string | null {
	const production = formatDayRange(row.minProductionDays, row.maxProductionDays);
	const delivery = formatDayRange(row.deliveryDaysMin, row.deliveryDaysMax);
	if (!production && !delivery) return null;
	return `${production ?? '—'} + ${delivery ?? '—'} days`;
}

/**
 * Every catalogue sample price on a product, in the form's currency — the one
 * fetch behind both sample forms.
 *
 * Rows are kept only for the product asked about: switching products changes
 * the query, and a response still in flight for the previous one must never
 * reach the new product's sample.
 */
export function useCatalogSamples(
	productId: string | null | undefined,
	currency: CurrencyEnum,
	skip = false
): CatalogSampleRow[] {
	const { data } = useGetProductSamplePricesQuery({
		variables: { input: { productId: productId ?? '' }, currency },
		skip: skip || !productId,
		fetchPolicy: 'no-cache',
	});
	return (data?.getProductSamplePrices ?? []).filter((row) => row.productId === productId);
}

/** Prices are compared to the cent: a figure read back through another currency can differ by less. */
const sameMoney = (a: number | null, b: number | null) =>
	a != null && b != null && Math.abs(a - b) < 0.005;

/**
 * Every catalogue sample price on the product, to pick one from.
 *
 * All of them are shown, not just the one that applies — a supplier's price
 * being invisible until they are assigned read as missing data. The assignment
 * decides what is USABLE: the default price always, a supplier's only once that
 * supplier is on the offer, since anything else would quote a figure they never
 * gave for it. The best match is marked; the form fills itself from it while the
 * sample is still empty, and any usable row can be taken instead.
 *
 * Built like the Sample payment picker above it — a labelled box of radio rows —
 * so the two read as one form: the radio shows which price the form holds, and
 * clicking a row applies it.
 */
export function SampleCatalogPicker({
	rows,
	assignedVetterId,
	formCurrency,
	current,
	onPick,
}: {
	rows: CatalogSampleRow[];
	assignedVetterId?: string | null;
	formCurrency: string;
	/** What the form holds now, to mark the row it came from. */
	current: { samplePrice: number | null; shippingPrice: number | null };
	onPick: (fill: SampleFill) => void;
}) {
	if (rows.length === 0) return null;

	const best = bestSampleRow(rows, assignedVetterId);
	/* The ONE row the form holds. Several can match the same figures (a default
	   and a supplier at the same price); the best match wins, then the first. */
	const holds = (row: CatalogSampleRow) => {
		const fill = sampleRowFill(row, formCurrency);
		return (
			!!fill &&
			sameMoney(current.samplePrice, fill.baseSamplePrice) &&
			(fill.baseShippingPrice == null ||
				sameMoney(current.shippingPrice, fill.baseShippingPrice))
		);
	};
	const appliedId = (best && holds(best) ? best : rows.find(holds))?.id ?? null;
	// Default first, then suppliers by name — the order of the catalogue page.
	const ordered = [...rows].sort(
		(a, b) =>
			Number(!!a.vetterEntityId) - Number(!!b.vetterEntityId) ||
			(a.vetterEntity?.name ?? '').localeCompare(b.vetterEntity?.name ?? '')
	);

	return (
		<div className="flex flex-col gap-1">
			<div className="text-sm font-medium text-text-1">Catalogue price</div>
			<div className="overflow-hidden rounded-lg border border-line-2">
				{ordered.map((row, i) => {
					const fill = sampleRowFill(row, formCurrency);
					const usable = isEligible(row, assignedVetterId) && !!fill;
					const applied = row.id === appliedId;
					const detail = !isEligible(row, assignedVetterId)
						? 'Assign this supplier to use it'
						: !fill
							? `No ${formCurrency} rate — enter it by hand`
							: formatLeadTime(row);
					// The figures the row shows: in the form's currency, or as stored without a rate.
					const price = fill?.baseSamplePrice ?? row.samplePrice;
					const shipping = fill ? fill.baseShippingPrice : (row.shippingPrice ?? null);
					const shownIn = fill ? formCurrency : row.currency;

					return (
						<button
							key={row.id}
							type="button"
							disabled={!usable}
							onClick={() => fill && onPick(fill)}
							className={cn(
								'flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors',
								i > 0 && 'border-t border-line-2',
								usable
									? 'cursor-pointer hover:bg-surface-hover'
									: 'cursor-not-allowed'
							)}
							style={{ background: applied ? 'var(--accent-soft)' : undefined }}
						>
							<span className={cn('mt-0.5', !usable && 'opacity-40')}>
								<Radio checked={applied} />
							</span>
							<span
								className={cn(
									'flex min-w-0 flex-1 flex-col',
									!usable && 'opacity-55'
								)}
							>
								<span className="flex items-center gap-1.5">
									<span className="truncate text-sm text-text-1">
										{row.vetterEntity?.name ?? 'Default price'}
									</span>
									{row.id === best?.id && (
										<span className="shrink-0 rounded-full border border-line-2 px-1.5 text-2xs text-text-3">
											Best match
										</span>
									)}
								</span>
								{detail && <span className="text-xs text-text-4">{detail}</span>}
							</span>
							<span
								className={cn(
									'flex shrink-0 flex-col items-end',
									!usable && 'opacity-55'
								)}
							>
								<CurrencyText
									amount={price}
									currency={shownIn}
									className="text-sm text-text-1"
								/>
								{shipping != null && (
									<span className="text-xs text-text-4">
										+ <CurrencyText amount={shipping} currency={shownIn} />{' '}
										shipping
									</span>
								)}
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
}
